import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import FeedbackForm from "@/pages/about/components/FeedbackForm";

const scopeItems = [
  { icon: "ri-play-circle-line", title: "在线播放演示", desc: "提供影片播放界面的前端演示，帮助你直观了解观影体验。" },
  { icon: "ri-book-open-line", title: "剧情介绍", desc: "完整的剧情梗概、制作背景与影片基本信息整理。" },
  { icon: "ri-quill-pen-line", title: "影评与专题", desc: "汇集影迷与影评人的深度解读，多角度读懂作品。" },
  { icon: "ri-newspaper-line", title: "影视资讯", desc: "影视动态、幕后花絮与行业观察，持续更新。" },
];

const stats = [
  { value: "4", label: "核心内容板块" },
  { value: "9+", label: "精选影评文章" },
  { value: "8+", label: "影视资讯条目" },
  { value: "1", label: "专注的经典作品" },
];

export default function About() {
  return (
    <div className="w-full">
      <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden bg-background-100">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20elegant%20dark%20cinema%20hall%20with%20warm%20amber%20lights%20and%20red%20velvet%20curtains%2C%20moody%20atmospheric%20nostalgic%20atmosphere%2C%20no%20text&width=1920&height=720&seq=about-hero-banner&orientation=landscape"
          alt="关于色戒在线观看"
          title="关于色戒在线观看"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/30" />
        <div className="absolute inset-0 w-full px-4 md:px-8 flex items-end pb-8 md:pb-12">
          <div className="w-full">
            <nav className="flex items-center gap-2 text-xs text-secondary-500 mb-3">
              <Link to="/" className="hover:text-accent-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">关于我们</h1>
            <p className="mt-3 text-sm md:text-base text-foreground-700 max-w-2xl">
              一个专注于经典电影《色戒》的影视门户，为电影爱好者提供剧情、影评与资讯。
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg bg-background-100 border border-background-200 p-5 text-center">
              <p className="font-heading text-3xl md:text-4xl font-bold text-accent-500">{s.value}</p>
              <p className="mt-2 text-xs md:text-sm text-secondary-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          <div>
            <SectionHeading title="网站定位" subtitle="我们想做的事" />
            <div className="mt-5 space-y-4 text-sm md:text-[15px] leading-loose text-foreground-700">
              <p>
                「色戒在线观看」是一个围绕经典电影《色戒》打造的影视内容门户。我们希望为喜爱这部电影的观众，
                提供一个可以集中了解剧情、阅读影评、获取资讯的地方。
              </p>
              <p>
                在这里，你既能快速回顾影片的故事脉络，也能读到来自影迷与影评人的多元解读；
                既可以体验模拟的播放界面，也能顺着推荐发现更多同类型的作品。
              </p>
              <p className="rounded-lg bg-background-100 border border-background-200 p-4 text-xs md:text-sm text-secondary-500 leading-relaxed">
                <i className="ri-information-line mr-1.5 text-accent-500" />
                需要说明的是：本站为内容演示站点，播放功能为前端模拟，不提供、也不存储任何真实的影视资源；
                站内所有影片简介、影评、资讯与推荐内容均为虚构示例，仅供学习与交流使用。
              </p>
            </div>
          </div>

          <div>
            <SectionHeading title="内容范围" subtitle="我们在站点上提供的内容" />
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {scopeItems.map((item) => (
                <div key={item.title} className="rounded-lg bg-background-100 border border-background-200 p-5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500/15 text-accent-500">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-3.5 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-xs md:text-sm text-secondary-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-1">
            <SectionHeading title="联系我们" subtitle="欢迎反馈与建议" />
            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-3 rounded-lg bg-background-100 border border-background-200 p-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/15 text-accent-500 shrink-0">
                  <i className="ri-mail-line text-lg" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-secondary-500">客服邮箱</p>
                  <p className="text-sm text-foreground-900 truncate">service@example-demo.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-background-100 border border-background-200 p-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/15 text-accent-500 shrink-0">
                  <i className="ri-time-line text-lg" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-secondary-500">回复时间</p>
                  <p className="text-sm text-foreground-900">工作日 24 小时内回复</p>
                </div>
              </div>
              <p className="text-xs text-secondary-500 leading-relaxed px-1">
                <i className="ri-information-line mr-1" />
                以上联系方式为演示信息，提交的反馈将通过站内表单统一收集。
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <SectionHeading title="反馈与建议" subtitle="你的每一条建议，都会被认真阅读" />
            <div className="mt-5">
              <FeedbackForm />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}